import React, { useEffect, useState, useRef } from 'react';
import { CITY_PRESETS, LAST_CITY_KEY } from '../../lib/appConstants';
import { CityPreset, ToastType } from '../../types/types';
import ProfileChip from './ProfileChip';
import ProfileView from './ProfileView';
import DecorativeCircles from './DecorativeCircles';
import { useAuth } from '../../contexts/AuthContext';
import LegalDocument from '../Legal/LegalDocument';
import {
  TERMS_SECTIONS,
  PRIVACY_SECTIONS,
  TERMS_EFFECTIVE,
  PRIVACY_EFFECTIVE,
  PRIVACY_UPDATED,
} from '../../assets/legal/legalContent';

interface CityPickerProps {
  mode?: 'blocking' | 'drawer';
  onClose?: () => void;
  onCitySelected: (city: CityPreset) => void;
  contextText?: string;
  showToast?: (message: string, type?: ToastType, duration?: number, dismissible?: boolean) => void;
  onRefreshCalendar?: () => void;
}

const TAGLINES = ['Build habits.', 'Connect with community.'];

const CityPicker: React.FC<CityPickerProps> = ({
  mode = 'blocking',
  onClose,
  onCitySelected,
  contextText,
  showToast,
  onRefreshCalendar,
}) => {
  const { isAuthenticated } = useAuth();
  const [taglineIndex, setTaglineIndex] = useState(0);
  const [taglineVisible, setTaglineVisible] = useState(true);
  const [contentVisible, setContentVisible] = useState(true);
  const [isVisible, setIsVisible] = useState(mode !== 'drawer');
  const [showProfile, setShowProfile] = useState(false);

  const [legalView, setLegalView] = useState<'terms' | 'privacy' | null>(null);

  const selectedCityRef = useRef<CityPreset | null>(null);
  const dragStartYRef = useRef<number | null>(null);
  const cityListRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (mode !== 'drawer') return;
    const frame = requestAnimationFrame(() => setIsVisible(true));
    return () => cancelAnimationFrame(frame);
  }, [mode]);

  // Tagline flip loop — stops once content starts fading out (city selected)
  useEffect(() => {
    if (!contentVisible) return;
    const interval = setInterval(() => {
      setTaglineVisible(false);
      setTimeout(() => {
        setTaglineIndex(i => (i + 1) % TAGLINES.length);
        setTaglineVisible(true);
      }, 400);
    }, 2800);
    return () => clearInterval(interval);
  }, [contentVisible]);

  const handleCityClick = (city: CityPreset) => {
    localStorage.setItem(LAST_CITY_KEY, JSON.stringify(city));
    selectedCityRef.current = city;
    setContentVisible(false);
    setIsVisible(false);
    setTimeout(() => onCitySelected(city), 900);
  };

  const handleRandomCity = () => {
    const random = CITY_PRESETS[Math.floor(Math.random() * CITY_PRESETS.length)];
    handleCityClick(random);
  };

  const handleDrawerClose = () => {
    setIsVisible(false);
    setTimeout(() => onClose?.(), 900);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="citypicker-heading"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: mode === 'drawer' ? 99999 : 1000,
        transform: isVisible ? 'translateY(0)' : 'translateY(-100%)',
        transition: 'transform 0.9s cubic-bezier(0.4, 0, 0.2, 1)',
        overflow: 'hidden',
        backgroundColor: 'var(--color-surface-subtle)',
      }}
    >
      <DecorativeCircles />

      {/* Drag handle — pinned to bottom, bounces to invite upward swipe */}
      {mode === 'drawer' && (
        <>
          <style>{`
            @keyframes bounce-hint {
              0%, 100% { transform: translateY(0); }
              40% { transform: translateY(-8px); }
              60% { transform: translateY(-4px); }
            }
          `}</style>
          <div
            role="button"
            tabIndex={0}
            aria-label="close city picker"
            onTouchStart={e => {
              dragStartYRef.current = e.touches[0].clientY;
            }}
            onTouchEnd={e => {
              if (dragStartYRef.current === null) return;
              const dragDistance = dragStartYRef.current - e.changedTouches[0].clientY;
              if (dragDistance > 50) {
                handleDrawerClose();
              }
              dragStartYRef.current = null;
            }}
            onClick={handleDrawerClose}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleDrawerClose();
              }
            }}
            style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: 4,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '16px 0 24px',
              cursor: 'grab',
              minHeight: 'unset', // override mobile.css [role="button"] { min-height: 44px }
              minWidth: 'unset',
            }}
          >
            <div
              style={{
                width: '40px',
                height: '5px',
                borderRadius: '3px',
                backgroundColor: 'var(--color-border-strong)',
                animation: 'bounce-hint 1.4s ease-in-out 3',
              }}
            />
          </div>
        </>
      )}

      {/* Content — scrollable in both modes so the email form doesn't overflow */}
      <div
        style={{
          position: 'relative',
          zIndex: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-start',
          height: mode === 'drawer' ? 'calc(100% - 56px)' : '100%',
          overflowY: 'auto',
          padding: mode === 'drawer' ? '24px 28px 16px' : '48px 28px 48px',
          opacity: contentVisible ? 1 : 0,
          transition: 'opacity 0.4s ease',
          pointerEvents: contentVisible ? 'auto' : 'none',
        }}
        onTouchStart={mode === 'drawer' ? e => e.stopPropagation() : undefined}
      >
        {/* Tagline */}
        <div
          style={{
            marginBottom: '48px',
            textAlign: 'center',
            minHeight: '64px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <p
            id="citypicker-heading"
            style={{
              fontSize: '14px',
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: 'var(--color-content-muted)',
              marginBottom: '10px',
              fontWeight: 500,
              margin: '0 0 10px 0',
            }}
          >
            Welcome to ceen
          </p>
          <p
            style={{
              fontSize: '21px',
              fontWeight: 700,
              color: 'var(--color-content)',
              margin: 0,
              opacity: taglineVisible ? 1 : 0,
              transform: taglineVisible ? 'translateY(0)' : 'translateY(6px)',
              transition: 'opacity 0.4s ease, transform 0.4s ease',
              letterSpacing: '-0.01em',
            }}
          >
            {TAGLINES[taglineIndex]}
          </p>
        </div>

        {/* Drawer mode: optional context text (e.g. "sign in to save lists") */}
        {mode === 'drawer' && contextText && !isAuthenticated && (
          <p
            style={{
              color: 'var(--color-content-muted)',
              fontSize: '13px',
              letterSpacing: '0.04em',
              margin: '0 0 12px 0',
              textAlign: 'center',
              width: '100%',
              maxWidth: '320px',
            }}
          >
            {contextText}
          </p>
        )}

        {/* Drawer mode: profile chip (logged in) or Google sign-in (logged out) */}
        {mode === 'drawer' && (
          <div style={{ width: '100%', maxWidth: '320px', marginBottom: '20px' }}>
            <ProfileChip onOpenProfile={() => setShowProfile(true)} showToast={showToast} />
          </div>
        )}

        {/* City buttons */}
        <div
          ref={cityListRef}
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            width: '100%',
            maxWidth: '320px',
          }}
        >
          {mode === 'drawer' && (
            <p
              style={{
                margin: '0 0 4px 2px',
                fontSize: '11px',
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: 'var(--color-content-muted)',
                fontWeight: 500,
              }}
            >
              Switch city
            </p>
          )}

          {CITY_PRESETS.map(city => (
            <button
              key={city.id}
              onClick={() => handleCityClick(city)}
              style={{
                width: '100%',
                padding: '16px 24px',
                fontSize: '17px',
                fontWeight: 600,
                color: 'var(--color-content)',
                backgroundColor: 'var(--color-surface)',
                border: '1px solid var(--color-border)',
                borderRadius: '14px',
                boxShadow: 'var(--shadow-sm)',
                cursor: 'pointer',
                letterSpacing: '0.01em',
                transition: 'background-color 0.15s ease',
              }}
              onMouseEnter={e => {
                (e.currentTarget as HTMLButtonElement).style.backgroundColor =
                  'var(--color-surface-subtle)';
              }}
              onMouseLeave={e => {
                (e.currentTarget as HTMLButtonElement).style.backgroundColor =
                  'var(--color-surface)';
              }}
            >
              {city.name}
            </button>
          ))}

          {/* Random city button */}
          <button
            onClick={handleRandomCity}
            style={{
              width: '100%',
              padding: '14px 24px',
              fontSize: '15px',
              fontWeight: 500,
              color: 'var(--color-content-muted)',
              backgroundColor: 'transparent',
              border: '1px solid var(--color-border)',
              borderRadius: '14px',
              cursor: 'pointer',
              letterSpacing: '0.02em',
              transition: 'color 0.15s ease, border-color 0.15s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
            }}
            onMouseEnter={e => {
              (e.currentTarget as HTMLButtonElement).style.color = 'var(--color-content)';
              (e.currentTarget as HTMLButtonElement).style.borderColor =
                'var(--color-border-strong)';
            }}
            onMouseLeave={e => {
              (e.currentTarget as HTMLButtonElement).style.color = 'var(--color-content-muted)';
              (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--color-border)';
            }}
          >
            Drop me anywhere
          </button>
        </div>

        {/* Blocking mode: profile chip below cities */}
        {mode !== 'drawer' && (
          <div style={{ width: '100%', maxWidth: '320px', marginTop: '24px' }}>
            <div
              style={{
                width: '32px',
                height: '2px',
                borderRadius: '1px',
                backgroundColor: 'var(--color-primary)',
                margin: '0 auto 20px',
              }}
            />
            <ProfileChip onOpenProfile={() => setShowProfile(true)} showToast={showToast} />
          </div>
        )}

        {/* Legal footer */}
        <div
          style={{
            width: '100%',
            maxWidth: '320px',
            marginTop: '28px',
            paddingTop: '14px',
            borderTop: `1px solid ${'var(--color-border)'}`,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '10px',
            paddingBottom: mode === 'drawer' ? '8px' : '0',
          }}
        >
          <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
            {(['Terms of Use', 'Privacy Policy'] as const).map(label => (
              <button
                key={label}
                onClick={() => setLegalView(label === 'Terms of Use' ? 'terms' : 'privacy')}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0,
                  fontSize: '11px',
                  letterSpacing: '0.08em',
                  color: 'var(--color-content-muted)',
                  fontWeight: 400,
                  minHeight: 'unset',
                  minWidth: 'unset',
                  textDecoration: 'underline',
                  textDecorationColor: 'var(--color-border)',
                }}
              >
                {label}
              </button>
            ))}
            <a
              href="mailto:support@ceen.live"
              style={{
                fontSize: '11px',
                letterSpacing: '0.08em',
                color: 'var(--color-content-muted)',
                fontWeight: 400,
                textDecoration: 'underline',
                textDecorationColor: 'var(--color-border)',
              }}
            >
              Contact
            </a>
          </div>
          <p
            style={{
              margin: 0,
              fontSize: '10px',
              letterSpacing: '0.06em',
              color: 'var(--color-content-muted)',
              opacity: 0.7,
            }}
          >
            © 2026 ceen
          </p>
        </div>
      </div>

      {/* Legal document overlay — slides up over the picker */}
      <LegalDocument
        visible={legalView !== null}
        title={legalView === 'privacy' ? 'Privacy Policy' : 'Terms & Conditions'}
        subtitle={legalView === 'privacy' ? 'Privacy Policy' : 'Terms of Use'}
        effectiveDate={legalView === 'privacy' ? PRIVACY_EFFECTIVE : TERMS_EFFECTIVE}
        updatedDate={legalView === 'privacy' ? PRIVACY_UPDATED : undefined}
        sections={legalView === 'privacy' ? PRIVACY_SECTIONS : TERMS_SECTIONS}
        onClose={() => setLegalView(null)}
      />

      {/* Profile panel — slides in from the right over CityPicker */}
      <ProfileView
        isVisible={showProfile}
        onBack={() => setShowProfile(false)}
        showToast={showToast}
        onRefreshCalendar={onRefreshCalendar}
        onSwitchCity={() => setShowProfile(false)}
      />
    </div>
  );
};

export default CityPicker;
